import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { DataTable, StatusBadge } from "../../../components/Common/PageWidgets";
import TopUpModal from "../../../components/Common/TopUpModal";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import whiteArrow from "../../../assets/images/user-dashboard/white-right-arrow.svg";
import linkIcon from "../../../assets/images/support/link-white.svg";
import walletIcon from "../../../assets/images/sidebar/wallet-orange.svg";
import connectedBadge from "../../../assets/images/user-dashboard/connected-badge.svg";
import "../../../assets/scss/custom/components/top-rewards-widgets.scss";

// Stripe
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

//Import API
import endpoints from "../../../api/endpoints";
import { get, post, del } from "../../../api/axios";

//Utils
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const moneyToCents = ({ amount, cents }) => {
  const centsNumber = toFiniteNumber(cents);
  if (centsNumber !== null) return Math.round(centsNumber);

  const amountNumber = toFiniteNumber(amount);
  if (amountNumber !== null) return Math.round(amountNumber * 100);

  return 0;
};

const firstMoneyCents = (balanceData, fields) => {
  for (const { amount, cents } of fields) {
    if (
      toFiniteNumber(balanceData?.[amount]) !== null
      || toFiniteNumber(balanceData?.[cents]) !== null
    ) {
      return moneyToCents({
        amount: balanceData?.[amount],
        cents: balanceData?.[cents],
      });
    }
  }

  return 0;
};

const formatUsdFromCents = (cents) => USD_FORMATTER.format(cents / 100);

const formatReadableLabel = (value) => (
  (value || "Unknown")
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const getTransactionDate = (tx) => tx.transaction_date || tx.completed_at || tx.created_at;

const formatDate = (dateString, { includeTime = false } = {}) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Unknown";

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime ? {
      hour: '2-digit',
      minute: '2-digit',
    } : {}),
  });
};

const getTransactionAmountCents = (tx) => moneyToCents({
  amount: tx.amount,
  cents: tx.amount_cents,
});

const formatSignedTransactionAmount = (tx) => {
  const amountCents = Math.abs(getTransactionAmountCents(tx));
  const direction = (tx.type || "").toString().toLowerCase();
  const prefix = direction === "credit" ? "+" : direction === "debit" ? "-" : "";
  return `${prefix}${formatUsdFromCents(amountCents)}`;
};

const TypeBadge = ({ type }) => {
  const normalizedType = (type || "").toString().toLowerCase();
  return <StatusBadge status={normalizedType === "debit" ? "Debit" : "Credit"} />;
};

TypeBadge.propTypes = {
  type: PropTypes.string,
};

TypeBadge.defaultProps = {
  type: "credit",
};

const TRANSACTION_STATUS_TONE = {
  succeeded: "green",
  success: "green",
  completed: "green",
  paid: "green",
  pending: "orange",
  processing: "orange",
  payment_failed: "red",
  failed: "red",
  canceled: "grey",
  cancelled: "grey",
};

const TransactionStatusBadge = ({ status }) => {
  const normalizedStatus = (status || "unknown").toString().toLowerCase();
  const tone = TRANSACTION_STATUS_TONE[normalizedStatus] || "grey";
  return (
    <span className={`status-badge status-badge--${tone}`}>
      {formatReadableLabel(status)}
    </span>
  );
};

TransactionStatusBadge.propTypes = {
  status: PropTypes.string,
};

TransactionStatusBadge.defaultProps = {
  status: "unknown",
};

const BalanceBlock = ({ label, valueCents, helperText, highlight }) => (
  <div className={`wallet-balance-item${highlight ? ' wallet-balance-item--available' : ''}`}>
    <p className="wallet-balance-item__label">{label}</p>
    <p className="wallet-balance-item__value">{formatUsdFromCents(valueCents)}</p>
    <p className="wallet-balance-item__helper">{helperText}</p>
  </div>
);

BalanceBlock.propTypes = {
  label: PropTypes.string.isRequired,
  valueCents: PropTypes.number.isRequired,
  helperText: PropTypes.string.isRequired,
  highlight: PropTypes.bool,
};

BalanceBlock.defaultProps = {
  highlight: false,
};

const balanceValuePropType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

const WalletBalanceCard = ({ balanceData }) => {
  document.title = "Wallet | Omaha Food Lovers";

  const { 
    restaurant, 
    connect_account,
  } = balanceData || {};
  const availableBalanceCents = moneyToCents({
    amount: balanceData?.available_balance,
    cents: balanceData?.available_balance_cents,
  });
  const pendingStripeBalanceCents = moneyToCents({
    amount: balanceData?.pending_balance,
    cents: balanceData?.pending_balance_cents,
  });
  const pendingSettlementBalanceCents = firstMoneyCents(balanceData, [
    { amount: "pending_settlement_balance", cents: "pending_settlement_balance_cents" },
    { amount: "pending_platform_fee", cents: "pending_platform_fee_cents" },
    { amount: "pending_platform_fees", cents: "pending_platform_fees_cents" },
    { amount: "platform_fee_pending", cents: "platform_fee_pending_cents" },
  ]);
  const remainingBalanceCents = availableBalanceCents - pendingSettlementBalanceCents;

  return (
   <Card className="reward-card reward-card--account reward-card--wallet" style={{ height: '100%' }}>
  <CardBody>
    <div className="reward-card__eyebrow">
      {connect_account?.connected ? 'Connected' : 'Not Connected'}
    </div>
    <h2 className="reward-card__title">Wallet Balance</h2>
    {restaurant?.name && (
      <p className="reward-card__desc">{restaurant.name}</p>
    )}

    <div className="reward-card__body" />

    <div className="wallet-balance-grid">
      <BalanceBlock
        label="Available Balance"
        valueCents={availableBalanceCents}
        helperText="Funds currently available in Stripe."
        highlight
      />
      <BalanceBlock
        label="Pending Stripe Balance"
        valueCents={pendingStripeBalanceCents}
        helperText="Successful funds still processing in Stripe."
      />
      <BalanceBlock
        label="Pending Settlement Balance"
        valueCents={pendingSettlementBalanceCents}
        helperText="Platform fees awaiting settlement. Not yet deducted."
      />
      <BalanceBlock
        label="Remaining Balance"
        valueCents={remainingBalanceCents}
        helperText="Estimated available balance after settlement."
      />
    </div>

    <div className="wallet-balance-footer">
      <div className="wallet-balance-footer__info">
        <span className="wallet-balance-footer__label">Charges Enabled:</span>
        <span className={`wallet-balance-footer__value ${connect_account?.charges_enabled ? 'wallet-balance-footer__value--enabled' : 'wallet-balance-footer__value--disabled'}`}>
          {connect_account?.charges_enabled ? 'Yes' : 'No'}
        </span>
      </div>
      <div className="wallet-balance-footer__info">
        <span className="wallet-balance-footer__label">Payouts Enabled:</span>
        <span className={`wallet-balance-footer__value ${connect_account?.payouts_enabled ? 'wallet-balance-footer__value--enabled' : 'wallet-balance-footer__value--disabled'}`}>
          {connect_account?.payouts_enabled ? 'Yes' : 'No'}
        </span>
      </div>
    </div>
  </CardBody>
</Card>
  );
};

WalletBalanceCard.propTypes = {
  balanceData: PropTypes.shape({
    restaurant: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    connect_account: PropTypes.shape({
      connected: PropTypes.bool,
      charges_enabled: PropTypes.bool,
      payouts_enabled: PropTypes.bool,
    }),
    available_balance: balanceValuePropType,
    available_balance_cents: balanceValuePropType,
    pending_balance: balanceValuePropType,
    pending_balance_cents: balanceValuePropType,
    pending_settlement_balance: balanceValuePropType,
    pending_settlement_balance_cents: balanceValuePropType,
    pending_platform_fee: balanceValuePropType,
    pending_platform_fee_cents: balanceValuePropType,
    pending_platform_fees: balanceValuePropType,
    pending_platform_fees_cents: balanceValuePropType,
    platform_fee_pending: balanceValuePropType,
    platform_fee_pending_cents: balanceValuePropType,
  }),
};

/* -------------------------------------------------------------------------
 * Recent Activity Table Columns
 * ---------------------------------------------------------------------- */

const activityColumns = [
  {
    key: "dateLabel",
    label: "Date",
    width: "1fr",
  },
  {
    key: "description",
    label: "Description",
    width: "1.5fr",
    render: (r) => r.description || 'Wallet transaction',
  },
  {
    key: "type",
    label: "Type",
    width: "0.7fr",
    render: (r) => <TypeBadge type={r.type} />,
  },
  {
    key: "amount",
    label: "Amount",
    width: "0.8fr",
    align: "right",
    render: (r) => formatSignedTransactionAmount(r),
  },
  {
    key: "status",
    label: "Status",
    width: "0.8fr",
    render: (r) => <TransactionStatusBadge status={r.status} />,
  },
];

const TOP_UP_POLL_INTERVAL_MS = 2000;
const TOP_UP_POLL_TIMEOUT_MS = 18000;

const getTopUpPaymentIntentId = (tx) => (
  tx.payment_intent_id
  || tx.stripe_payment_intent_id
  || tx.payment?.payment_intent_id
  || tx.payment?.id
  || tx.payment_intent?.id
  || tx.meta?.payment_intent_id
  || null
);

const isCompletedTopUpStatus = (status) => {
  const normalizedStatus = (status || "").toString().toLowerCase();
  return ["succeeded", "success", "completed", "paid"].includes(normalizedStatus);
};

const getTransactionRows = (responseData) => {
  const payload = responseData?.data || responseData || {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.transactions)) return payload.transactions;
  if (Array.isArray(responseData?.transactions)) return responseData.transactions;
  return [];
};

const transformWalletTransaction = (tx) => ({
  id: tx.id,
  payment_intent_id: getTopUpPaymentIntentId(tx),
  type: (tx.type || "").toString().toLowerCase(),
  source: tx.source || "",
  description: tx.description || 'Wallet transaction',
  status: tx.status || 'pending',
  amount_cents: getTransactionAmountCents(tx),
  amount: Math.abs(getTransactionAmountCents(tx)) / 100,
  currency: tx.currency || 'usd',
  dateLabel: formatDate(getTransactionDate(tx), { includeTime: true }),
  transaction_date: tx.transaction_date,
  created_at: tx.created_at,
  completed_at: tx.completed_at,
  meta: {
    requested_by: tx.requested_by?.name,
    email: tx.requested_by?.email,
    description: tx.description,
    restaurant_name: tx.restaurant?.name,
    payment_intent_id: getTopUpPaymentIntentId(tx),
  },
});

/* -------------------------------------------------------------------------
 * RestaurantWalletPage
 * ---------------------------------------------------------------------- */

const RestaurantWalletPage = () => {
  document.title = "Wallet | Omaha Food Lovers";
  
  // Balance state
  const [balanceData, setBalanceData] = useState(null);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Stripe Connect state
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [unlinkingAccount, setUnlinkingAccount] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showUnlinkConfirmModal, setShowUnlinkConfirmModal] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const topUpPollingRef = useRef(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Load Stripe with network status check
  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_KEY;
    if (stripeKey && navigator.onLine) {
      loadStripe(stripeKey)
        .then(setStripe)
        .catch((error) => {
          console.error('Failed to load Stripe:', error);
        });
    }
  }, []);

  // Fetch restaurant profile
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantMe);
      if (response.data.success) {
        const data = response.data.data;
        const restaurant = data.current_restaurant;
        setRestaurantProfile(restaurant);
      }
    } catch (err) {
      console.error("Failed to fetch restaurant profile:", err);
    }
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fetch Stripe account status
  const fetchStripeAccountStatus = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantStripeConnectAccountsRefresh);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setStripeAccountStatus({
          account_id: data.account_id,
          onboarding_status: data.onboarding_status,
          payouts_enabled: data.payouts_enabled || false,
          charges_enabled: data.charges_enabled || false,
        });
        
        // If account is fully activated, set linked account
        if (data.onboarding_status === 'completed' && data.payouts_enabled && data.charges_enabled) {
          setLinkedAccount({
            stripe_account_id: data.account_id,
            name: 'Stripe Account',
            last4: '****',
            cashbackPercent: 0,
            processor: 'Stripe',
          });
        }
      }
    } catch (err) {
      setStripeAccountStatus(null);
    }
  }, []);

  // Check account status on mount
  useEffect(() => {
    fetchStripeAccountStatus();
  }, [fetchStripeAccountStatus]);

  // Generate new activation link
  const handleGenerateActivationLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await get(endpoints.restaurantStripeConnectAccountsActivationLink);
      
      if (response.data.success && response.data.data.activation_url) {
        window.location.href = response.data.data.activation_url;
      } else {
        toastr.error("Failed to generate activation link");
      }
    } catch (err) {
      console.error("Failed to generate activation link:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Handle link account
  const handleLinkAccount = async () => {
    setLinkingAccount(true);
    try {
      const response = await post(endpoints.restaurantStripeConnectAccounts, {});
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        setStripeAccountStatus({
          account_id: data.account_id,
          onboarding_status: data.onboarding_status,
          payouts_enabled: data.payouts_enabled || false,
          charges_enabled: data.charges_enabled || false,
        });
        
        if (data.activation_url) {
          window.location.href = data.activation_url;
        } else if (data.onboarding_status === 'completed' && data.payouts_enabled && data.charges_enabled) {
          toastr.success("Stripe account already activated!");
          setLinkedAccount({
            stripe_account_id: data.account_id,
            name: 'Stripe Account',
            last4: '****',
            cashbackPercent: 0,
            processor: 'Stripe',
          });
        } else {
          toastr.info("Account exists but requires completion. Generating activation link...");
          handleGenerateActivationLink();
        }
      } else {
        toastr.error("Failed to initiate account linking");
      }
    } catch (err) {
      console.error("Failed to link account:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setLinkingAccount(false);
    }
  };

  // Handle unlink account
  const handleUnlinkAccount = async () => {
    if (!linkedAccount?.stripe_account_id) return;
    setShowUnlinkConfirmModal(true);
  };

  // Confirm unlink account
  const confirmUnlinkAccount = async () => {
    if (!linkedAccount?.stripe_account_id) return;
    
    setUnlinkingAccount(true);
    setShowUnlinkConfirmModal(false);
    try {
      const url = endpoints.restaurantStripeConnectAccount.replace('{{connect_account_id}}', linkedAccount.stripe_account_id);
      const response = await del(url);
      
      if (response.data.success) {
        toastr.success("Account unlinked successfully!");
        setLinkedAccount(null);
        setStripeAccountStatus(null);
        fetchBalance();
      }
    } catch (err) {
      console.error("Failed to unlink account:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setUnlinkingAccount(false);
    }
  };

  // Fetch balance from API
  const fetchBalance = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await get(endpoints.restaurantStripeConnectBalance);
      if (response.data.success && response.data.data) {
        setBalanceData(response.data.data);
        return response.data.data;
      }
    } catch (err) {
      console.error("Failed to fetch balance:", err);
      if (!silent) {
        const errorMessage = extractErrorMessage(err);
        toastr.error(errorMessage);
      }
      // Use null as fallback
      if (!silent) setBalanceData(null);
    }
    return null;
  }, []);

  // Fetch balance on mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Fetch transactions from API
  const fetchTransactions = useCallback(async ({ showLoading = true, silent = false } = {}) => {
    if (showLoading) setLoadingTransactions(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        per_page: "10",
      });

      const response = await get(`${endpoints.restaurantStripeConnectTransactions}?${params.toString()}`);
      
      if (response.data.success) {
        const transformedTransactions = getTransactionRows(response.data).map(transformWalletTransaction);
        
        setTransactions(transformedTransactions);
        return transformedTransactions;
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      if (!silent) {
        const errorMessage = extractErrorMessage(err);
        toastr.error(errorMessage);
      }
      // Use empty array as fallback
      if (!silent) setTransactions([]);
    } finally {
      if (showLoading) setLoadingTransactions(false);
    }
    return [];
  }, []);

  const fetchTopUpsForSync = useCallback(async () => {
    const response = await get(`${endpoints.restaurantStripeConnectTopups}?page=1&page_size=10`);
    if (!response.data.success) return [];
    return getTransactionRows(response.data).map(transformWalletTransaction);
  }, []);

  // Fetch transactions on mount
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const stopTopUpPolling = useCallback(() => {
    if (topUpPollingRef.current?.timerId) {
      clearInterval(topUpPollingRef.current.timerId);
    }
    topUpPollingRef.current = null;
  }, []);

  const findMatchingTopUp = useCallback((topUpContext, refreshedTransactions) => {
    if (!Array.isArray(refreshedTransactions) || !refreshedTransactions.length) return null;

    const { fundingId, paymentIntentId } = topUpContext || {};
    if (paymentIntentId) {
      const paymentIntentMatch = refreshedTransactions.find((tx) => tx.payment_intent_id === paymentIntentId);
      if (paymentIntentMatch) return paymentIntentMatch;
    }

    if (fundingId) {
      const fundingIdMatch = refreshedTransactions.find((tx) => Number(tx.id) === Number(fundingId));
      if (fundingIdMatch) return fundingIdMatch;
    }

    return refreshedTransactions[0] || null;
  }, []);

  const refreshWalletAfterTopUp = useCallback(async (topUpContext, { silent = false } = {}) => {
    const [, , refreshedTopUps] = await Promise.all([
      fetchBalance({ silent }),
      fetchTransactions({ showLoading: false, silent }),
      fetchTopUpsForSync(),
    ]);

    return findMatchingTopUp(topUpContext, refreshedTopUps);
  }, [fetchBalance, fetchTransactions, fetchTopUpsForSync, findMatchingTopUp]);

  const startTopUpSync = useCallback(async (topUpContext = {}) => {
    stopTopUpPolling();
    fetchStripeAccountStatus();

    try {
      const initialMatch = await refreshWalletAfterTopUp(topUpContext, { silent: false });
      if (isCompletedTopUpStatus(initialMatch?.status)) return;
    } catch (error) {
      toastr.warning("Payment was confirmed, but wallet refresh is taking longer than expected.");
    }

    const startedAt = Date.now();
    topUpPollingRef.current = {
      timerId: setInterval(async () => {
        if (Date.now() - startedAt >= TOP_UP_POLL_TIMEOUT_MS) {
          stopTopUpPolling();
          toastr.info("Payment was confirmed. Transaction status may take a moment to update.");
          return;
        }

        try {
          const matchingTopUp = await refreshWalletAfterTopUp(topUpContext, { silent: true });
          if (isCompletedTopUpStatus(matchingTopUp?.status)) {
            stopTopUpPolling();
          }
        } catch (error) {
          // Keep the bounded poll alive; the next interval may succeed.
        }
      }, TOP_UP_POLL_INTERVAL_MS),
    };
  }, [fetchStripeAccountStatus, refreshWalletAfterTopUp, stopTopUpPolling]);

  useEffect(() => () => stopTopUpPolling(), [stopTopUpPolling]);

  const emptyTransactionMessage = "No wallet transactions found.";

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="g-3">
          <Col xl={8}>
            <WalletBalanceCard
              balanceData={balanceData}
            />
          </Col>
          <Col xl={4}>
            <Card className="reward-card reward-card--streak" style={{ height: '95%' }}>
              <CardBody>
                <div className="reward-card__eyebrow">
                  {linkedAccount && stripeAccountStatus?.onboarding_status === 'completed' ? "Account Linked" : "Account Settings"}
                </div>
                <h2 className="reward-card__title">
                  {linkedAccount && stripeAccountStatus?.onboarding_status === 'completed' ? (
                    <div className="reward-card__title-with-check">
                      Account Connected
                      <img
                        src={connectedBadge}
                        alt="connected"
                        className="reward-check-icon"
                      />
                    </div>
                  ) : stripeAccountStatus?.onboarding_status === 'requirements_due' ? (
                    "Complete Setup"
                  ) : (
                    "Link Your Account"
                  )}
                </h2>
                
                {linkedAccount && stripeAccountStatus?.onboarding_status === 'completed' ? (
                  <>
                    <p className="reward-card__desc">Your Stripe bank account is connected</p>
                    <p className="reward-card__desc">
                      Powered securely by {linkedAccount.processor}
                    </p>
                    <div className="reward-card__body" />
                    <div className="reward-card__footer-row">
                      <div className="reward-account-wrapper">
                        <div className="reward-account-label">Account</div>
                        <div className="reward-account-number">
                          ************{stripeAccountStatus.account_id?.slice(-4) || '****'}
                        </div>
                      </div>
                      <SecondaryButton 
                        label="Add Funds" 
                        variant="light-orange" 
                        icon={walletIcon} 
                        onClick={() => setShowTopUpModal(true)}
                      />
                    </div>
                    <div className="reward-card__footer-row" >
                      <div />
                      <SecondaryButton 
                        label="Unlink Account" 
                        variant="orange" 
                        icon={linkIcon} 
                        onClick={handleUnlinkAccount} 
                        disabled={unlinkingAccount}
                        loading={unlinkingAccount}
                      />
                    </div>
                  </>
                ) : stripeAccountStatus?.onboarding_status === 'requirements_due' ? (
                  <>
                    <p className="reward-card__desc">
                      Your Stripe account requires additional information to be activated. Please complete the onboarding process to start receiving settlements.
                    </p>
                    <div className="reward-card__body" />
                    <div className="reward-card__footer-row">
                      <div />
                      <SecondaryButton 
                        label="Complete Setup" 
                        variant="orange" 
                        icon={linkIcon} 
                        onClick={handleGenerateActivationLink} 
                        disabled={generatingLink || linkingAccount}
                        loading={generatingLink || linkingAccount}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <p className="reward-card__desc">
                      Connect your Stripe account to enable secure payments and settlements. This allows you to receive payments from customers directly to your restaurant.
                    </p>
                    <div className="reward-card__body" />
                    <div className="reward-card__footer-row">
                      <img src={walletIcon} alt="wallet" />
                      <SecondaryButton 
                        label="Link Account" 
                        variant="orange" 
                        icon={linkIcon} 
                        onClick={handleLinkAccount} 
                        disabled={linkingAccount}
                        loading={linkingAccount}
                      />
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Transaction History</h2>
          <div className="wallet-section-header__right">
            
            <Link to="/restaurant/transactions">
              <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
        </div>

        <div className="activity-list">
          {loadingTransactions ? (
            <DataTable 
              columns={activityColumns} 
              rows={[]} 
              getRowKey={(row) => row.id}
              loading={true}
            />
          ) : transactions && transactions.length > 0 ? (
            <DataTable 
              columns={activityColumns} 
              rows={transactions.slice(0, 10)} 
              getRowKey={(row) => row.id}
            />
          ) : (
            <p className="vouchers-page__empty">{emptyTransactionMessage}</p>
          )}
        </div>
      </Container>

      {stripe && (
        <Elements stripe={stripe}>
          <TopUpModal
            isOpen={showTopUpModal}
            onClose={() => setShowTopUpModal(false)}
            vendorListingId={restaurantProfile?.id}
            onSuccess={startTopUpSync}
            onPaymentFailure={() => {
              fetchTransactions({ showLoading: false, silent: true });
            }}
          />
        </Elements>
      )}

      {/* Unlink Account Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showUnlinkConfirmModal}
        title="Unlink Account"
        message="Are you sure you want to unlink your Stripe account? This action cannot be undone."
        onCancel={() => setShowUnlinkConfirmModal(false)}
        onConfirm={confirmUnlinkAccount}
        confirmButtonLoading={unlinkingAccount}
      />
    </div>
  );
};

export default RestaurantWalletPage;
